import React, { useEffect, useRef, useState } from 'react';
import { Loader, Sparkles, ChevronDown, ChevronUp } from './Icons';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { AgentMessage } from '../types';

interface AgentOutputProps {
  messages: AgentMessage[];
  isCollapsed: boolean;
  isProcessing: boolean;
}

interface MessageProps {
  message: AgentMessage;
}

const MessageDisplay: React.FC<MessageProps> = ({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 200; // Characters before truncation
  const shouldTruncate = message.content.length > maxLength;

  const getAvatarStyle = () => {
    switch (message.role) {
      case 'user':
        return 'bg-gray-700 text-white';
      case 'error':
        return 'bg-red-100 text-red-600';
      case 'system':
        return 'bg-green-100 text-green-600';
      case 'FIELD_EXTRACTOR':
        return 'bg-purple-100 text-purple-600';
      case 'FIELD_EXTRACTOR_VISION':
        return 'bg-indigo-100 text-indigo-600';
      case 'FIELD_VALIDATOR':
        return 'bg-orange-100 text-orange-600';
      case 'ANOMALY_DETECTOR':
        return 'bg-red-100 text-red-600';
      case 'INVOICE_TYPE_CLASSIFIER':
        return 'bg-teal-100 text-teal-600';
      case 'FIELD_LEVEL_CLASSIFIER':
        return 'bg-cyan-100 text-cyan-600';
      case 'CRITICAL_FIELDS_VALIDATOR':
        return 'bg-amber-100 text-amber-600';
      case 'FIELD_DERIVER':
        return 'bg-lime-100 text-lime-600';
      case 'INVOICE_DIGITIZER':
        return 'bg-emerald-100 text-emerald-600';
      case 'REPORT_GENERATOR':
        return 'bg-sky-100 text-sky-600';
      case 'USER_FEEDBACK':
        return 'bg-rose-100 text-rose-600';
      case 'REVALIDATION_INTENT_DETECTOR':
        return 'bg-violet-100 text-violet-600';
      case 'PO_MISMATCH_WORKFLOW':
        return 'bg-fuchsia-100 text-fuchsia-600';
      case 'NON_PO_WORKFLOW_TRIGGER':
        return 'bg-pink-100 text-pink-600';
      case 'GR_WORKFLOW_TRIGGER':
        return 'bg-slate-100 text-slate-600';
      case 'EXCEPTION_HANDLER':
        return 'bg-stone-100 text-stone-600';
      case 'WATCH_TOWER':
        return 'bg-purple-100 text-purple-600';
      case 'Invoice Flow Agent':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getAvatarText = () => {
    switch (message.role) {
      case 'user':
        return 'U';
      case 'error':
        return '!';
      case 'system':
        return 'S';
      case 'FIELD_EXTRACTOR':
        return 'FE';
      case 'FIELD_VALIDATOR':
        return 'FV';
      case 'ANOMALY_DETECTOR':
        return 'AD';
      case 'WATCH_TOWER':
        return 'WT';
      default:
        const words = message.role.trim().split(/[_\s]+/).filter(Boolean);
        const initials = words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
        return initials || 'NA';
    }
  };

  const getRoleLabel = () => {
    switch (message.role) {
      case 'user':
        return 'User';
      case 'error':
        return 'Error';
      case 'system':
        return 'System';
      case 'FIELD_EXTRACTOR':
        return 'Field Extraction Agent';
      case 'FIELD_EXTRACTOR_VISION':
        return 'Field Extraction Vision Agent';
      case 'FIELD_VALIDATOR':
        return 'Field Validation Agent';
      case 'ANOMALY_DETECTOR':
        return 'Anomaly Detection Agent';
      case 'INVOICE_TYPE_CLASSIFIER':
        return 'Invoice Type Classifier Agent';
      case 'FIELD_LEVEL_CLASSIFIER':
        return 'Field Level Classifier Agent';
      case 'CRITICAL_FIELDS_VALIDATOR':
        return 'Critical Fields Validator Agent';
      case 'FIELD_DERIVER':
        return 'Field Deriver Agent';
      case 'INVOICE_DIGITIZER':
        return 'Invoice Digitizer Agent';
      case 'REPORT_GENERATOR':
        return 'Report Generator Agent';
      case 'USER_FEEDBACK':
        return 'User Feedback Agent';
      case 'REVALIDATION_INTENT_DETECTOR':
        return 'Revalidation Intent Detector Agent';
      case 'PO_MISMATCH_WORKFLOW':
        return 'PO Mismatch Workflow Agent';
      case 'NON_PO_WORKFLOW_TRIGGER':
        return 'Non-PO Workflow Trigger Agent';
      case 'GR_WORKFLOW_TRIGGER':
        return 'GR Workflow Trigger Agent';
      case 'EXCEPTION_HANDLER':
        return 'Exception Handler Agent';
      case 'WATCH_TOWER':
        return 'Watch Tower Agent';
      case 'Invoice Flow Agent':
        return 'Invoice Flow Agent';
      default:
        return message.role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()).replace(/\s+Agent$/, '') + ' Agent';;
    }
  };

  return (
    <div className="flex gap-3 mb-4 animate-fade-in-up">
      <Avatar className="h-8 w-8">
        <AvatarFallback className={`text-xs font-medium ${getAvatarStyle()}`}>
          {getAvatarText()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-smibold text-sm text-gray-500 dark:text-gray-100 transition-colors duration-200">{getRoleLabel()}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-200">
            {new Date().toLocaleTimeString()}
          </span>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3 text-xs text-gray-700 dark:text-gray-200 break-all whitespace-pre-line leading-relaxed transition-colors duration-200 overflow-x-auto">
          {shouldTruncate && !isExpanded
            ? `${message.content.substring(0, maxLength)}...`
            : message.content
          }
        </div>
        {shouldTruncate && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                Show more
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export const AgentOutput: React.FC<AgentOutputProps> = ({
  messages,
  isCollapsed,
  isProcessing
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (isCollapsed) {
    return (
      <div className="h-full bg-white dark:bg-gray-800 flex flex-col items-center py-4 transition-colors duration-200">
        <div className="text-xs text-gray-600 dark:text-gray-300 writing-mode-vertical transition-colors duration-200">Agent Huddle</div>
        {isProcessing && (
          <div className="mt-4">
            <Loader className="w-4 h-4 text-blue-500 dark:text-blue-400 animate-spin" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-gray-800 flex flex-col transition-colors duration-200">
      {/* Header */}
      <div className="p-1 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">Agent Huddle</h3>
          </div>
          {isProcessing && (
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg transition-colors duration-200">
              <Loader className="w-3 h-3 text-blue-500 dark:text-blue-400 animate-spin" />
              <span className="text-xs text-blue-700 dark:text-blue-300 transition-colors duration-200">Processing...</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-4">
          <div className="space-y-4">
            {messages.length === 0 && !isProcessing && (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <div className="text-sm">No agent activity yet</div>
                  <div className="text-xs mt-1 text-gray-400">AI agents will appear here during processing</div>
                </div>
              </div>
            )}

            {messages.map((message, index) => {
              if (message.role === 'WATCH_TOWER') {
                console.log('Rendering Watch Tower message in AgentOutput:', message);
              }
              return <MessageDisplay key={index} message={message} />;
            })}

            {isProcessing && messages.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Loader className="w-8 h-8 mx-auto mb-2 text-blue-500 animate-spin" />
                  <div className="text-gray-600 text-sm">Initializing AI agents...</div>
                  <div className="text-xs text-gray-400 mt-1">This may take a few moments</div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};